import React from 'react';
import { Card, VStack } from '@adc/parallax-component-library';
import DynamicForm, {
  DynamicFormConfig,
  DynamicFormFieldType,
} from '../../form/DynamicForm';
import { Sensor, UpdateRequest } from '../../../models';
import {
  ProductType,
  SensorStatus,
  EntityState,
} from 'adc-sirius-module-common';
import * as Yup from 'yup';
import i18n from '../../../utilities/i18n';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  sensorQueryOptions,
  updateSensorMutateOptions,
} from '../../../queryOptions';
import { mapProductTypeToString } from '../../../utilities/mappers';

type SensorHeaderProps = {
  sensor: Sensor;
  deviceId: string;
};

type SensorFormValues = Sensor &
  UpdateRequest & {
    deviceId: string;
    lifecount: number;
    displayedProductType: string;
    displayedSensorState: string;
  };

/**
 * SensorHeader component displays the header information for a specific sensor.
 * @param {Sensor} sensor - The sensor object containing details to display.
 * @param {string} deviceId - The device ID associated with the sensor.
 * @returns {React.JSX.Element}
 */
export default function SensorHeader({ sensor, deviceId }: SensorHeaderProps) {
  const queryClient = useQueryClient();

  const mapFormValuesToSensor = (values: SensorFormValues): Sensor => {
    const { newUpdateReason, ...updatedValues } = values;
    const updatedSensor = {
      ...sensor,
      ...updatedValues,
      updateReason: newUpdateReason,
    };

    return updatedSensor;
  };

  const mapSensorToFormValues = (sensor: Sensor): SensorFormValues => ({
    ...sensor,
    newUpdateReason: '',
    deviceId,
    lifecount: sensor.analyteCounts[0]?.current || 0,
    activationTime: sensor.activationTime,
    displayedSensorState: SensorStatus[sensor.sensorState],
    displayedProductType: mapProductTypeToString(sensor.productType),
  });

  const formConfig: DynamicFormConfig<SensorFormValues> = {
    fields: [
      {
        name: 'serialNumber',
        label: i18n.t('Page_Sensor.Form.serialNumber'),
        type: DynamicFormFieldType.TEXT,
        isEditable: false,
      },
      {
        name: 'deviceId',
        label: i18n.t('Page_Sensor.Form.deviceId'),
        type: DynamicFormFieldType.TEXT,
        isEditable: false,
      },
      {
        name: 'conditionCode',
        label: i18n.t('Page_Sensor.Form.conditionId'),
        type: DynamicFormFieldType.TEXT,
        isEditable: true,
      },
      {
        name: 'lifecount',
        label: i18n.t('Page_Sensor.Form.lifecount'),
        type: DynamicFormFieldType.TEXT,
        isEditable: false,
      },
      {
        name: 'isMdcCapable',
        label: i18n.t('Page_Sensor.Form.isMdcCapable'),
        type: DynamicFormFieldType.CHECKBOX,
        checkboxOptions: {
          uncheckedLabel: i18n.t('Common.no'),
          checkedLabel: i18n.t('Common.yes'),
        },
        isEditable: false,
      },
      {
        name: 'isPrimaryReceiver',
        label: i18n.t('Page_Sensor.Form.isPrimaryReceiver'),
        type: DynamicFormFieldType.CHECKBOX,
        checkboxOptions: {
          uncheckedLabel: i18n.t('Common.no'),
          checkedLabel: i18n.t('Common.yes'),
        },
        isEditable: false,
      },
      {
        name: 'newUpdateReason',
        label: i18n.t('Page_Sensor.Form.newUpdateReason'),
        type: DynamicFormFieldType.TEXT,
        isEditable: true,
        isEditOnly: true,
      },
      {
        name: 'displayedProductType',
        label: i18n.t('Page_Sensor.Form.productType'),
        type: DynamicFormFieldType.TEXT,
        isEditable: false,
      },
      {
        name: 'displayedSensorState',
        label: i18n.t('Page_Sensor.Form.sensorState'),
        type: DynamicFormFieldType.TEXT,
        isEditable: false,
      },
      {
        name: 'activationTime',
        label: i18n.t('Page_Sensor.Form.activatedTime'),
        type: DynamicFormFieldType.DATE,
        isEditable: false,
      },
      {
        name: 'createdBy',
        label: i18n.t('Page_Sensor.Form.createdBy'),
        type: DynamicFormFieldType.TEXT,
      },
      {
        name: 'createdOn',
        label: i18n.t('Page_Sensor.Form.createdOn'),
        type: DynamicFormFieldType.DATE,
      },
      {
        name: 'updatedBy',
        label: i18n.t('Page_Sensor.Form.updatedBy'),
        type: DynamicFormFieldType.TEXT,
      },
      {
        name: 'updatedOn',
        label: i18n.t('Page_Sensor.Form.updatedOn'),
        type: DynamicFormFieldType.DATE,
      },
      {
        name: 'updateReason',
        label: i18n.t('Page_Sensor.Form.updateReason'),
        type: DynamicFormFieldType.TEXT,
      },
    ],
    validationSchema: {
      key: Yup.string().required(i18n.t('Form.genericRequired')),
      serialNumber: Yup.string().required(i18n.t('Form.genericRequired')),
      conditionCode: Yup.string(),
      activationTime: Yup.number().required(i18n.t('Form.genericRequired')),
      deviceId: Yup.string(),
      lifecount: Yup.number(),
      newUpdateReason: Yup.string().required(i18n.t('Form.genericRequired')),
      createdBy: Yup.string(),
      createdOn: Yup.string(),
      updatedBy: Yup.string(),
      updatedOn: Yup.string(),
      updateReason: Yup.string(),
      userNotes: Yup.array(),
      deviceKey: Yup.string(),
      productType: Yup.mixed().oneOf(Object.values(ProductType)),
      displayedProductType: Yup.string(),
      warmupDuration: Yup.number(),
      wearDuration: Yup.number(),
      firmwareVersion: Yup.string(),
      state: Yup.mixed().oneOf(Object.values(EntityState)),
      lastEvent: Yup.number(),
      analytes: Yup.array(),
      analyteCounts: Yup.array(),
      isPrimaryReceiver: Yup.boolean(),
      isMdcCapable: Yup.boolean(),
      type: Yup.string(),
      sensorState: Yup.mixed().oneOf(Object.values(SensorStatus)),
      displayedSensorState: Yup.string(),
    },
    initialValues: {
      ...mapSensorToFormValues(sensor),
    },
  };

  const updateSensor = useMutation(updateSensorMutateOptions());

  const handleSubmit = async (values: Record<string, any>) => {
    const sensorValues = mapFormValuesToSensor(values as SensorFormValues);

    return updateSensor
      .mutateAsync({
        sensorKey: sensor.key,
        updatedSensorValues: sensorValues,
      })
      .then((updatedSensor) => {
        const sensorQueryKey = sensorQueryOptions({
          key: updatedSensor.key,
        }).queryKey;

        queryClient.setQueryData(sensorQueryKey, updatedSensor);

        const updatedFormValues = mapSensorToFormValues(updatedSensor);

        return updatedFormValues;
      })
      .catch(() => {
        throw new Error(i18n.t('Page_Sensor.update_error_generic'));
      });
  };

  return (
    <VStack flexGrow={1} gap={'$4'} testID="sensor-details">
      <Card>
        <DynamicForm
          id="sensor-details"
          title={i18n.t('Page_Sensor.title')}
          formConfig={formConfig}
          maxRows={Math.ceil(formConfig.fields.length / 2)}
          onSave={handleSubmit}
        />
      </Card>
    </VStack>
  );
}
